import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, AlertCircle, ExternalLink, ArrowRight, Clock } from "lucide-react";
import { toast } from "sonner";

// Map raw Stripe requirement keys to French human-readable labels.
// Stripe sends keys like "individual.id_number", "external_account", "tos_acceptance.date", etc.
// Source: https://stripe.com/docs/connect/required-verification-information
const STRIPE_REQ_LABELS = {
  "external_account": "Compte bancaire pour recevoir les paiements",
  "tos_acceptance.date": "Acceptation des Conditions de service Stripe",
  "tos_acceptance.ip": "Acceptation des Conditions de service Stripe (IP)",
  "business_profile.url": "Site web de l'entreprise",
  "business_profile.mcc": "Code d'activité (catégorie commerciale)",
  "business_profile.product_description": "Description du produit/service",
  "individual.id_number": "Numéro d'assurance sociale (NAS) complet — 9 chiffres",
  "individual.ssn_last_4": "4 derniers chiffres du NAS",
  "individual.verification.document": "Pièce d'identité (photo recto-verso du permis ou passeport)",
  "individual.verification.additional_document": "Document additionnel d'identité (Stripe demande un 2e justificatif)",
  "individual.address.line1": "Adresse personnelle (rue)",
  "individual.address.city": "Ville",
  "individual.address.postal_code": "Code postal",
  "individual.address.state": "Province",
  "individual.phone": "Numéro de téléphone",
  "individual.email": "Adresse courriel",
  "individual.dob.day": "Date de naissance — jour",
  "individual.dob.month": "Date de naissance — mois",
  "individual.dob.year": "Date de naissance — année",
  "individual.first_name": "Prénom",
  "individual.last_name": "Nom de famille",
  "individual.political_exposure": "Question politique (PPE)",
  "company.tax_id": "Numéro d'entreprise",
  "company.verification.document": "Document de vérification de l'entreprise",
  "settings.payouts.statement_descriptor": "Libellé qui apparaîtra sur le relevé bancaire",
};
function translateStripeReq(key) {
  return STRIPE_REQ_LABELS[key] || key;
}

/**
 * StripeConnectCard - Express onboarding via Account Links.
 * Displays the host's payout setup status with clear next steps.
 */
export default function StripeConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get("/connect/status");
      setStatus(data);
    } catch {
      setStatus({ connected: false });
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // Auto-refresh if user just came back from Stripe onboarding
    if (searchParams.get("stripe") === "onboarded") {
      toast.success("Compte Stripe configuré avec succès !");
      const next = new URLSearchParams(searchParams);
      next.delete("stripe");
      setSearchParams(next, { replace: true });
    } else if (searchParams.get("stripe") === "refresh") {
      toast.info("Reprise de la configuration Stripe...");
      const next = new URLSearchParams(searchParams);
      next.delete("stripe");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startOnboarding = async () => {
    setStarting(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/connect/onboarding-link", {
        return_url: `${origin}/profile?stripe=onboarded`,
        refresh_url: `${origin}/profile?stripe=refresh`,
      });
      if (data?.onboarding_url) {
        window.location.href = data.onboarding_url;
      } else {
        toast.error("URL Stripe introuvable");
        setStarting(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la configuration Stripe");
      setStarting(false);
    }
  };

  const openDashboard = async () => {
    try {
      const { data } = await api.get("/connect/login-link");
      if (data?.url) window.open(data.url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir déconnecter votre compte Stripe ? Vous ne pourrez plus publier de terrain tant que vous ne le reconnectez pas.")) return;
    try {
      await api.post("/connect/disconnect");
      toast.success("Compte Stripe déconnecté");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // Restart the whole flow: disconnect the old (possibly mis-emailed) account then create a fresh one.
  // Useful when Stripe pre-fills an old wrong email or "Utilisateur introuvable".
  const restartOnboarding = async () => {
    if (!window.confirm("Cela va supprimer le compte Stripe en cours et en créer un nouveau avec votre email actuel. Continuer ?")) return;
    setStarting(true);
    try {
      await api.post("/connect/disconnect");
      const origin = window.location.origin;
      const { data } = await api.post("/connect/onboarding-link", {
        return_url: `${origin}/profile?stripe=onboarded`,
        refresh_url: `${origin}/profile?stripe=refresh`,
      });
      if (data?.onboarding_url) {
        window.location.href = data.onboarding_url;
      } else {
        toast.error("URL Stripe introuvable");
        setStarting(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <Card className="border border-zinc-200 rounded-2xl">
        <CardContent className="p-6 flex items-center gap-3 text-sm text-zinc-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Chargement du statut Stripe...
        </CardContent>
      </Card>
    );
  }

  // CASE 1: Not connected at all -> initial CTA
  if (!status?.connected) {
    return (
      <Card className="border border-amber-200 bg-amber-50 rounded-2xl" data-testid="stripe-connect-card">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <h3 className="font-heading font-bold text-zinc-950 mb-1">Configurez vos paiements</h3>
              <p className="text-sm text-zinc-700">
                Pour recevoir vos paiements de location, configurez votre compte Stripe. <strong>3-5 minutes</strong>, formulaire géré par Stripe.
              </p>
              <ul className="text-xs text-zinc-600 mt-3 space-y-1 list-disc list-inside">
                <li>Vos paiements arrivent automatiquement <strong>24 h après chaque séjour</strong></li>
                <li>Pas besoin de compte Stripe existant — créez-en un en 3 min</li>
                <li>Sécurisé : Stripe gère vos informations bancaires (PCI-DSS niveau 1)</li>
              </ul>
            </div>
          </div>
          <Button onClick={startOnboarding} disabled={starting} className="bg-[#635bff] hover:bg-[#5851e6] text-white rounded-xl w-full sm:w-auto" data-testid="connect-stripe-btn">
            {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
            Configurer mes paiements
          </Button>
        </CardContent>
      </Card>
    );
  }

  const fullyOnboarded = status.charges_enabled && status.payouts_enabled && status.details_submitted;

  // CASE 2: Account created but onboarding not complete -> resume
  if (!fullyOnboarded) {
    const req = status.requirements || {};
    const blockingItems = [...(req.past_due || []), ...(req.currently_due || [])];
    const pendingItems = req.pending_verification || [];
    return (
      <Card className="border border-amber-200 bg-amber-50 rounded-2xl" data-testid="stripe-resume-card">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <h3 className="font-heading font-bold text-zinc-950 mb-1">Onboarding incomplet</h3>
              <p className="text-sm text-zinc-700">
                Vous avez commencé la configuration de votre compte Stripe mais il manque encore des informations.
                Reprenez là où vous en étiez.
              </p>
              <ul className="text-xs text-zinc-600 mt-3 space-y-1">
                <li>{status.details_submitted ? "✅" : "⚠️"} Informations soumises</li>
                <li>{status.charges_enabled ? "✅" : "⚠️"} Paiements autorisés</li>
                <li>{status.payouts_enabled ? "✅" : "⚠️"} Virements bancaires actifs</li>
              </ul>

              {blockingItems.length > 0 && (
                <div className="mt-3 p-3 rounded-lg bg-white border border-amber-300" data-testid="stripe-blocking-items">
                  <p className="text-xs font-semibold text-amber-800 mb-1.5">⚠️ Stripe demande encore ces informations :</p>
                  <ul className="text-xs text-zinc-700 space-y-0.5">
                    {blockingItems.map((k) => (
                      <li key={k}>• <code className="text-[10px] bg-zinc-100 px-1 rounded">{translateStripeReq(k)}</code></li>
                    ))}
                  </ul>
                </div>
              )}

              {pendingItems.length > 0 && (
                <div className="mt-3 p-3 rounded-lg bg-white border border-blue-200" data-testid="stripe-pending-items">
                  <p className="text-xs font-semibold text-blue-800 mb-1.5">⏳ En cours de vérification par Stripe :</p>
                  <ul className="text-xs text-zinc-700 space-y-0.5">
                    {pendingItems.map((k) => (
                      <li key={k}>• <code className="text-[10px] bg-zinc-100 px-1 rounded">{translateStripeReq(k)}</code></li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-zinc-500 mt-1.5">Stripe revoit ces éléments. Cela peut prendre de quelques minutes à 48 h.</p>
                </div>
              )}

              {req.disabled_reason && (
                <p className="text-[11px] text-zinc-500 mt-2">
                  Motif technique Stripe : <code className="bg-zinc-100 px-1 rounded">{req.disabled_reason}</code>
                </p>
              )}

              {status.refresh_error && (
                <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-300" data-testid="stripe-refresh-error">
                  <p className="text-xs font-semibold text-red-800 mb-1">🔌 Impossible d'obtenir le statut depuis Stripe</p>
                  <p className="text-[11px] text-red-700 leading-relaxed">
                    Le serveur n'arrive pas à interroger votre compte. Cela arrive presque toujours quand la clé API Stripe en production ne correspond pas à celle qui a créé le compte (ex : compte créé en mode Test, clé actuelle en mode Live).
                  </p>
                  <p className="text-[10px] text-red-600 mt-1 font-mono break-all">{status.refresh_error}</p>
                  <p className="text-[11px] text-red-700 mt-2">
                    <strong>Solution :</strong> cliquez sur <em>« Annuler et déconnecter »</em> puis recréez votre compte Stripe (le formulaire ne prendra que 5 min).
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={startOnboarding} disabled={starting} className="bg-[#635bff] hover:bg-[#5851e6] text-white rounded-xl" data-testid="resume-stripe-btn">
              {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
              Reprendre la configuration
            </Button>
            <Button onClick={openDashboard} variant="outline" size="sm" className="rounded-xl" data-testid="open-stripe-dashboard-btn">
              <ExternalLink className="h-3 w-3 mr-1" /> Ouvrir Stripe Dashboard
            </Button>
            <Button onClick={restartOnboarding} disabled={starting} variant="outline" size="sm" className="rounded-xl" data-testid="restart-stripe-btn">
              Recommencer avec un autre email
            </Button>
            <Button onClick={handleDisconnect} variant="outline" size="sm" className="rounded-xl text-red-600 hover:text-red-700">
              Annuler et déconnecter
            </Button>
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed pt-1">
            💡 Si Stripe affiche &quot;Utilisateur introuvable&quot; ou un email qui n&apos;est pas le vôtre, cliquez sur <strong>&quot;Recommencer avec un autre email&quot;</strong> pour repartir à zéro avec votre adresse actuelle.
          </p>
        </CardContent>
      </Card>
    );
  }

  // CASE 3: Fully connected -> green status
  return (
    <Card className="border border-emerald-200 bg-emerald-50 rounded-2xl" data-testid="stripe-connected-card">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <h3 className="font-heading font-bold text-zinc-950 mb-1">Compte Stripe configuré</h3>
            <p className="text-sm text-zinc-700">
              Vos paiements arrivent automatiquement <strong>24 h après chaque séjour</strong> sur votre compte bancaire.
            </p>
            <p className="text-xs text-zinc-500 mt-2">
              ID : <code className="bg-white px-2 py-0.5 rounded text-xs border">{status.stripe_account_id}</code>
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={openDashboard} variant="outline" size="sm" className="rounded-xl" data-testid="stripe-dashboard-btn">
            <ExternalLink className="h-3 w-3 mr-1" /> Mon tableau de bord Stripe
          </Button>
          <Button onClick={handleDisconnect} variant="outline" size="sm" className="rounded-xl text-red-600 hover:text-red-700">
            Déconnecter
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
